// ABOUTME: Print-optimized report page for PDF generation via browser print.
// ABOUTME: Renders report with same layout as editor but without UI chrome.

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context';
import { apiClient } from '@/services/api';
import type { PrintReport, ReportSection } from '@/types/api';
import { ReadOnlySection } from '@/components/report/ReadOnlySection';
import { PageFooterWatermark, LastPageAttribution } from '@/components/report/PrintWatermark';
import '@/styles/globals.css';

/** Sort sections by their order field for single-column rendering. */
function sortSections(sections: ReportSection[]): ReportSection[] {
  return [...sections].sort((a, b) => a.order - b.order);
}

export function ReportPrintPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  console.log('[ReportPrintPage] Component rendered');
  console.log('[ReportPrintPage] Report ID:', id);
  console.log('[ReportPrintPage] Token present:', !!token);
  console.log('[ReportPrintPage] Token value:', token?.substring(0, 8));

  const { isLoading: authLoading, isAuthenticated } = useAuth();

  console.log('[ReportPrintPage] Auth state - loading:', authLoading, 'authenticated:', isAuthenticated);

  const [report, setReport] = useState<PrintReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  // Prevent concurrent fetches (one-time token can only be used once)
  const fetchingRef = useRef(false);

  const fetchReportWithToken = useCallback(async () => {
    console.log('[ReportPrintPage] fetchReportWithToken called');
    console.log('[ReportPrintPage] ID:', id, 'Token:', token?.substring(0, 8));
    console.log('[ReportPrintPage] fetchingRef.current:', fetchingRef.current);

    if (fetchingRef.current) {
      console.log('[ReportPrintPage] Already fetching, skipping duplicate request');
      return;
    }

    if (!id || !token) {
      console.log('[ReportPrintPage] Missing ID or token, setting error');
      setError('Missing report ID or token');
      return;
    }

    fetchingRef.current = true;
    try {
      console.log('[ReportPrintPage] Calling apiClient.reports.getWithPrintToken');
      // Use special endpoint that accepts token
      const fetchedReport = await apiClient.reports.getWithPrintToken(id, token);
      console.log('[ReportPrintPage] Report fetched successfully');
      console.log('[ReportPrintPage] Report data:', {
        id: fetchedReport.id,
        title: fetchedReport.title,
        sectionCount: fetchedReport.sections?.length || 0,
        hasSections: !!fetchedReport.sections,
      });
      setReport(fetchedReport);

      setTimeout(() => {
        console.log('[ReportPrintPage] Setting ready state and data-print-ready attribute');
        setReady(true);
        document.body.setAttribute('data-print-ready', 'true');
      }, 1000);
    } catch (err) {
      console.error('[ReportPrintPage] Failed to fetch report with token:', err);
      setError('Failed to load report');
    } finally {
      fetchingRef.current = false;
    }
  }, [id, token]);

  const fetchReport = useCallback(async () => {
    if (!id) {
      setError('No report ID provided');
      return;
    }
    try {
      const fetchedReport = await apiClient.reports.get(id);
      setReport(fetchedReport);

      // Signal that we're ready for print
      // Small delay to ensure charts render
      setTimeout(() => {
        setReady(true);
        document.body.setAttribute('data-print-ready', 'true');
      }, 1000);
    } catch (err) {
      console.error('Failed to fetch report:', err);
      setError('Failed to load report');
    }
  }, [id]);

  useEffect(() => {
    console.log('[ReportPrintPage] useEffect triggered');

    if (!id) {
      console.log('[ReportPrintPage] No ID provided, setting error');
      setError('No report ID provided');
      return;
    }

    // If token provided, skip auth check (Playwright PDF generation)
    if (token) {
      console.log('[ReportPrintPage] Token present, calling fetchReportWithToken');
      fetchReportWithToken();
      return;
    }

    // Normal authenticated user flow
    if (authLoading) {
      console.log('[ReportPrintPage] Auth loading, waiting...');
      return;
    }
    if (!isAuthenticated) {
      console.log('[ReportPrintPage] Not authenticated, setting error');
      setError('Please log in to view this report');
      return;
    }

    console.log('[ReportPrintPage] Calling fetchReport (authenticated user)');
    fetchReport();
  }, [id, token, authLoading, isAuthenticated, fetchReportWithToken, fetchReport]);

  if (error) {
    return (
      <div className="print-error">
        <p>{error}</p>
      </div>
    );
  }

  if (authLoading || !report) {
    return (
      <div className="print-loading">
        <p>{authLoading ? 'Authenticating...' : 'Loading report...'}</p>
      </div>
    );
  }

  const sections = sortSections(report.sections);

  return (
    <div className="print-container" data-ready={ready}>
      <style>{`
        @page {
          size: letter;
          margin: 0.5in;
        }

        @media print {
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }

        .print-container {
          width: 100%;
        }

        .print-page {
          width: 8.5in;
          padding: 0.25in;
          margin: 0 auto;
          background: white;
          box-sizing: border-box;
        }

        .print-header {
          margin-bottom: 1.5em;
          padding-bottom: 0.5em;
          border-bottom: 2px solid var(--color-primary-500, #14b8a6);
        }

        .print-header h1 {
          font-size: 24pt;
          margin: 0 0 0.25em 0;
          color: var(--color-surface-900, #1c1917);
        }

        .print-section {
          margin-bottom: 1em;
          break-inside: avoid;
        }

        .print-loading,
        .print-error {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          font-size: 14pt;
          color: var(--color-surface-500, #78716c);
        }
      `}</style>

      <div className="print-page" style={{ position: 'relative' }}>
        <div className="print-header">
          <h1>{report.title}</h1>
        </div>

        <div className="report-editor">
          {sections.map((section) => (
            <div key={section.id} className="print-section">
              <ReadOnlySection
                section={section}
                chartHeight={300}
                showChartLegend={true}
                maxTableRows={50}
              />
            </div>
          ))}
        </div>

        {/* Footer watermark (tier-gated) */}
        <PageFooterWatermark subscriptionTier={report.subscription_tier} />
      </div>

      {/* Last-page attribution (freemium only) */}
      <LastPageAttribution subscriptionTier={report.subscription_tier} />
    </div>
  );
}
